"use client";

import Link from "next/link";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
const heroFallbackImage = "/assets/feature-hero.jpg";

const Hero = () => {
  const { t } = useTranslation();
  const [videoErrored, setVideoErrored] = useState(false);
  return (
    <section className="relative h-[700px] flex items-center">
      {/* Video Background with Dark Overlay */}
      <div className="absolute inset-0 z-0">
        {!videoErrored ? (
          <video
            autoPlay
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover object-top"
            onLoadedMetadata={(e) => {
              // Start video at ~3 seconds
              e.currentTarget.currentTime = 3;
            }}
            onEnded={(e) => {
              // Loop back to 3 seconds instead of the beginning
              e.currentTarget.currentTime = 3;
              e.currentTarget.play();
            }}
            onError={() => {
              // If the browser can't decode/load the MP4, fall back to an image.
              console.warn("Hero video failed to load: /videos/hero-video.mp4");
              setVideoErrored(true);
            }}
          >
            {/* Cache-bust to avoid stale/cached responses after replacing the file */}
            <source src="/videos/hero-video.mp4?v=2" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={heroFallbackImage}
            alt="Virtual care consultation"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
        )}
        {/* Dark overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
      </div>

      {/* Content - Left aligned, vertically centered */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-16 w-full">
        <div className="max-w-xl">
          {/* Eyebrow Tag */}
          <span className="inline-block bg-accent-blue border border-accent-blue text-white text-sm font-button font-bold tracking-[0.14em] uppercase px-5 py-2 rounded-full mb-6 shadow-lg shadow-accent-blue/30">
            {t("hero.eyebrow")}
          </span>

          {/* Main Heading */}
          <h1 className="font-heading font-bold text-4xl lg:text-6xl !leading-[1.2] text-white mb-5">
            {t("hero.heading1")}
            <br />
            <span className="text-white">{t("hero.heading2")}</span>
          </h1>

          {/* Subheading */}
          <p className="font-body text-base lg:text-lg leading-relaxed text-white/90 mb-8">
            {t("hero.subheading")}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Link href="/individuals">
              <Button
                className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-5 py-3 font-button text-sm flex items-center gap-2 cta-hover cta-hover-glow"
              >
                {t("hero.ctaIndividuals")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link href="/businesses">
              <Button
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-5 py-3 font-button text-sm flex items-center gap-2 cta-hover cta-hover-glow-primary"
              >
                {t("hero.ctaBusinesses")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll to contact floating button */}
      <Link href="/contact" className="hidden lg:flex absolute bottom-16 right-10 w-12 h-12 bg-accent-blue hover:bg-accent-blue/90 rounded-full items-center justify-center shadow-lg transition-all hover:scale-105">
        <ArrowRight className="w-5 h-5 text-white" />
      </Link>
    </section>
  );
};

export default Hero;
